/**
 * Renderer registry.
 *
 * Renderers are looked up by name from `renderers` and rendered via
 * `renderByName`. Unknown names fall back to `JsonPreviewRenderer` with
 * a subtle warning so the UI stays functional if the backend introduces
 * a renderer the frontend hasn't shipped yet.
 *
 * Today, the synthetic-detail builder always emits sections with
 * `renderer: 'type_aware'` (see `buildSyntheticDetail.ts`). The other
 * core renderers (`key_value`, `markdown`, `json_preview`) remain
 * registered so direct callers and a future backend that returns
 * explicit renderer hints can dispatch to them by name.
 */

import * as React from 'react'
import type { CSSProperties } from 'react'
import { Suspense, lazy } from 'react'
import { AlertTriangle } from 'lucide-react'

// Direct imports for lightweight renderers
import { KeyValueRenderer } from './KeyValueRenderer'
import { TypeAwareRenderer } from './TypeAwareRenderer'
import { ExecutionSummaryRenderer } from './ExecutionSummaryRenderer'
import { OriginalEmailRenderer } from './OriginalEmailRenderer'
import { InteractionEmailRenderer } from './InteractionEmailRenderer'

// Bespoke renderers for the Log Feature flow (ticket_reporter agent).
// Imported eagerly so they're available the moment the modal opens
// without an additional Suspense boundary.
import { DraftTicketRenderer } from '@/features/log-feature/renderers/DraftTicketRenderer'
import { SubmittedTicketRenderer } from '@/features/log-feature/renderers/SubmittedTicketRenderer'
import { ExistingTicketRenderer } from '@/features/log-feature/renderers/ExistingTicketRenderer'

// Lazy-loaded heavier renderers
const MarkdownRenderer = lazy(() => import('./MarkdownRenderer'))
const JsonPreviewRenderer = lazy(() => import('./JsonPreviewRenderer'))

/**
 * Renderer component prop type.
 *
 * All renderers accept `data: unknown` and handle validation internally.
 */
export interface RendererProps {
  data: unknown
}

/**
 * Registry map of renderer names to components.
 */
export const renderers: Record<
  string,
  React.ComponentType<RendererProps>
> = {
  key_value: KeyValueRenderer,
  type_aware: TypeAwareRenderer,
  execution_summary: ExecutionSummaryRenderer,
  // Task 4054d: full-fidelity inbound-email section for send_email approvals.
  original_email: OriginalEmailRenderer,
  // Agent-sourced send_email approvals reference the inbound email by id only;
  // this renderer hydrates it on demand and delegates to OriginalEmailRenderer.
  interaction_email: InteractionEmailRenderer,
  markdown: MarkdownRenderer,
  json_preview: JsonPreviewRenderer,
  // Bespoke renderers used by the ticket_reporter agent's draft/submit flow.
  draft_ticket: DraftTicketRenderer,
  submitted_ticket: SubmittedTicketRenderer,
  existing_ticket: ExistingTicketRenderer,
}

/** Renderers loaded via React.lazy — must be wrapped in Suspense at render time. */
const LAZY_RENDERER_NAMES = new Set(['markdown', 'json_preview'])

/**
 * Fallback loading component for lazy-loaded renderers.
 */
function RendererSkeleton() {
  return (
    <div style={styles.skeleton} className="animate-pulse">
      <span style={styles.skeletonText}>Loading...</span>
    </div>
  )
}

/**
 * Unknown renderer fallback.
 *
 * Falls back to `<JsonPreviewRenderer />` with a subtle warning
 * ("Unknown renderer: ...; showing raw data"). This keeps the UI functional
 * when backend adds renderers the frontend hasn't shipped yet.
 */
function UnknownRenderer({ name, data }: { name: string; data: unknown }) {
  return (
    <div style={styles.unknownContainer}>
      <div
        role="alert"
        style={styles.warningAlert}
      >
        <AlertTriangle style={styles.warningIcon} aria-hidden="true" />
        <span style={styles.warningText}>
          Unknown renderer: <code style={styles.codeName}>{name}</code>; showing raw data
        </span>
      </div>
      <Suspense fallback={<RendererSkeleton />}>
        <JsonPreviewRenderer data={data} />
      </Suspense>
    </div>
  )
}

/**
 * Render a section using the named renderer.
 *
 * Lookup in the registry map; fallback to UnknownRenderer if not found.
 *
 * @param name - The renderer name from the registry (e.g., "type_aware")
 * @param data - The section data to render
 * @returns React element for the rendered section
 */
export function renderByName(name: string, data: unknown): React.ReactElement {
  const Renderer = renderers[name]

  if (!Renderer) {
    return <UnknownRenderer name={name} data={data} />
  }

  if (LAZY_RENDERER_NAMES.has(name)) {
    return (
      <Suspense fallback={<RendererSkeleton />}>
        <Renderer data={data} />
      </Suspense>
    )
  }

  return <Renderer data={data} />
}

/**
 * Get the list of registered renderer names.
 */
export function getRegisteredRenderers(): string[] {
  return Object.keys(renderers)
}

/**
 * Check if a renderer name is registered.
 */
export function hasRenderer(name: string): boolean {
  return name in renderers
}

const styles: Record<string, CSSProperties> = {
  skeleton: {
    display: 'flex',
    height: '96px',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border-default)',
    background: 'var(--bg-tertiary)',
  },
  skeletonText: {
    fontSize: '14px',
    color: 'var(--text-muted)',
  },
  unknownContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  warningAlert: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid rgba(245, 158, 11, 0.3)',
    background: 'rgba(245, 158, 11, 0.05)',
    padding: '8px 12px',
    fontSize: '14px',
  },
  warningIcon: {
    width: '16px',
    height: '16px',
    color: 'var(--warning)',
  },
  warningText: {
    color: 'var(--text-primary)',
  },
  codeName: {
    fontFamily: "'JetBrains Mono', monospace",
  },
}
